import React, { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  Pagination
} from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import PaymentAPI from 'api/faber/PaymentAPI'
import ColorBox from 'common/Box/ColorWhiteBox'
import ProfileListInfo from 'common/FaberComponents/Layout/FaberColumnLayout'
import TextColorFontBody from 'common/Typography/TextColorFontBody'
import format from 'date-fns/format'
import OrbitSession from 'hooks/useCookies'
import useModal from 'hooks/useModal'
import { RefundRequestsType } from 'types/api/faber/payment.type'

import NoPermissionSnackbar from './NoPermissionSnackbar'
import RefundUpdateStatusDialogV2 from './RefundUpdateStatusDialogV2'

function RefundRequestV2Tab({ customerId }: RefundProps) {
  const faberToken = OrbitSession.get('faberToken')
  const [pageOffset, setPageOffset] = useState(0)
  const [pageSize] = useState(5)
  const [page, setPage] = useState(1)
  const [rowCount, setRowCount] = useState(1)
  const [rows, setRows] = useState<RefundRequestsType[] | undefined>([])
  const [selectedRow, setSelectedRow] = useState<
    RefundRequestsType | undefined
  >()

  const refundStatusChecked = []
  const searchCriteria = 'Customer ID'
  const searchValue = useMemo(() => customerId, [customerId])

  const {
    closeModal: closeRefundStatusUpdateDialog,
    isShowing: isRefundDialogOpen
  } = useModal()

  const {
    closeModal: closePermissionSnackbar,
    isShowing: noPermissionSnackbarOpen
  } = useModal()

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    newPage: number
  ) => {
    const tmpOffset = (newPage - 1) * pageSize
    setPage(newPage)
    setPageOffset(tmpOffset)
  }

  const {
    isLoading: gettingAllCustomerPayments,
    refetch: refetchRefundRequests
  } = useQuery(
    ['getAllRefundRequests', [faberToken, pageOffset, pageSize, searchValue]],
    async () => {
      return PaymentAPI.getAllRefundRequests(
        pageOffset,
        pageSize,
        faberToken,
        refundStatusChecked,
        searchCriteria,
        searchValue
      )
    },
    {
      onError: () => {
        toast.error('Error getting payment details')
        setRows([])
      },
      onSuccess: (res) => {
        setRows(res.data.body.data)
        setRowCount(Math.ceil(res.data.body.total / pageSize))

        if (res.data.body.data) {
          setSelectedRow(
            res.data.body.data[Math.ceil(res.data.body.total / pageSize) - 1]
          )
        }
      }
    }
  )
  const handleRefundClicked = (request: RefundRequestsType) => {
    setSelectedRow(request)
  }

  useEffect(() => {
    refetchRefundRequests()
  }, [page])

  return gettingAllCustomerPayments || !rows ? (
    <CircularProgress />
  ) : (
    <Box sx={{ display: 'flex', gap: 2 }}>
      <>
        <ColorBox
          sx={{
            width: '45%',
            height: '90vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
          }}
        >
          <>
            <Box>
              {rows.length === 0 ? (
                <TextColorFontBody>No Refund Requests</TextColorFontBody>
              ) : (
                rows.map((req) => (
                  <Card
                    key={req.ID}
                    sx={{
                      mb: 3,
                      border: '1px solid #ECEFF4',
                      boxShadow: 'none'
                    }}
                  >
                    <CardActionArea onClick={() => handleRefundClicked(req)}>
                      <CardContent
                        sx={{
                          display: 'flex',
                          pb: 0,
                          alignItems: 'center',
                          backgroundColor:
                            req.ID === selectedRow?.ID
                              ? 'rgba(206,238,255,0.3)'
                              : 'white'
                        }}
                      >
                        <Grid container>
                          <Grid item xs={2}>
                            <TextColorFontBody
                              sx={{
                                mt: 1,
                                pr: 1,
                                color: '#818D9C',
                                fontSize: '0.8em'
                              }}
                            >
                              Refund Status
                            </TextColorFontBody>
                            <Box>
                              <TextColorFontBody
                                sx={{
                                  fontSize: '0.8em',
                                  fontWeight: 700,
                                  color:
                                    req.refundStatus === 'SUCCESS'
                                      ? '#33c759'
                                      : '#C733A1'
                                }}
                              >
                                {req.refundStatus}
                              </TextColorFontBody>
                            </Box>
                          </Grid>
                          <Grid item xs={10}>
                            <ProfileListInfo
                              title=''
                              fields={[
                                {
                                  fieldName: 'Payment ID',
                                  fieldValue: req.refundPaymentId
                                },
                                {
                                  fieldName: 'Created on',
                                  fieldValue: format(
                                    new Date(req.CreatedAt),
                                    'dd MMM yyyy'
                                  )
                                },
                                {
                                  fieldName: 'Created by',
                                  fieldValue: `${req.createdBy}`
                                }
                              ]}
                            />
                          </Grid>
                        </Grid>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                ))
              )}
            </Box>
            <Box>
              <Pagination
                hidePrevButton={page === 1}
                hideNextButton={rowCount === 1 || page === rowCount}
                count={rowCount}
                color='primary'
                page={page}
                onChange={handlePageChange}
              />
            </Box>
          </>
        </ColorBox>
        {rows.length > 0 && (
          <ColorBox sx={{ width: '50%', height: '90vh' }}>
            <>
              <Grid container>
                <ProfileListInfo
                  title='Refund Request Information'
                  fields={[
                    {
                      fieldName: 'Refund Payment ID',
                      fieldValue: selectedRow ? selectedRow.refundPaymentId : ''
                    },
                    {
                      fieldName: 'Refund Reason',
                      fieldValue: selectedRow ? selectedRow.refundReason : ''
                    }
                  ]}
                />
                <ProfileListInfo
                  title=''
                  fields={[
                    {
                      fieldName: 'Ticket ID',
                      fieldValue: selectedRow ? selectedRow.ticketId : ''
                    },
                    {
                      fieldName: 'Cancel Subscription',
                      fieldValue: selectedRow
                        ? selectedRow.terminateSub
                          ? 'Yes'
                          : 'No'
                        : ''
                    }
                  ]}
                />

                <ProfileListInfo
                  title=''
                  fields={[
                    {
                      fieldName: 'Created At',
                      fieldValue: selectedRow
                        ? format(
                            new Date(selectedRow.CreatedAt),
                            'dd MMM yyyy HH:mm'
                          )
                        : ''
                    },
                    {
                      fieldName: 'Last Updated At',
                      fieldValue: selectedRow
                        ? format(
                            new Date(selectedRow.UpdatedAt),
                            'dd MMM yyyy HH:mm'
                          )
                        : ''
                    }
                  ]}
                />
                <Divider sx={{ width: '100%', mt: 2 }} />
                <ProfileListInfo
                  title='Refund Requestor Information'
                  fields={[
                    {
                      fieldName: 'Created By',
                      fieldValue: selectedRow ? selectedRow.createdBy : ''
                    }
                  ]}
                />
              </Grid>
            </>
          </ColorBox>
        )}
        <NoPermissionSnackbar
          handlePermissionSnackbarClose={closePermissionSnackbar}
          permissionSnackbarOpen={noPermissionSnackbarOpen}
        />
        <RefundUpdateStatusDialogV2
          dialogOpen={isRefundDialogOpen}
          handleDialogClose={closeRefundStatusUpdateDialog}
          paymentId={
            selectedRow?.refundPaymentId ? selectedRow.refundPaymentId : ''
          }
          faberToken={faberToken}
        />
      </>
    </Box>
  )
}

export default RefundRequestV2Tab

interface RefundProps {
  customerId: string | undefined
}
